import React, {useEffect} from "react";
import {getVenueSession} from "../../requests/AppSession";
import {useNavigate} from "react-router-dom";
import {NumericFormat} from "react-number-format";
import {CircularProgress, Skeleton} from "@mui/material";
import LoginError from "../login/LoginError";
import {findPreviousStages} from "../requests/AppRequestStage";
import {useAuth0} from "@auth0/auth0-react";

/**
 * VTPreviousStage
 *
 * Static (non-live) venue page showing the last 5 previous stage sets: the dancer who was on stage,
 * their consolidated stage-tip total, and the table requests made during that stage session.
 * Data is historical and already persisted, so this page uses a plain REST fetch (no websockets).
 */
export default function VTPreviousStage() {
    let navigate = useNavigate();

    const {isLoading, getAccessTokenSilently} = useAuth0();

    const [venueId, setVenueId] = React.useState(null);
    const [previousStages, setPreviousStages] = React.useState(null);
    const [isPageLoading, setIsPageLoading] = React.useState(true);
    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.venueId && venueSession.venueId !== null) {
                setVenueId(venueSession.venueId);
            } else {
                navigate('/login/mobile', {replace: true});
            }
        });
        return function cleanup() {
        };
    }, []);

    const getSuccess = async responseJson => {
        setIsPageLoading(false);
        setIsRefreshing(false);
        if (responseJson) {
            setPreviousStages(responseJson);
        } else {
            setErrorMessage("Error loading previous stage data, please refresh and verify Internet Connection.");
        }
    };

    const getError = () => {
        setIsPageLoading(false);
        setIsRefreshing(false);
        setErrorMessage("Error loading previous stage data, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (!isLoading && venueId !== null) {
            findPreviousStages(venueId, getSuccess, getError, getAccessTokenSilently);
        }
        return function cleanup() {
        };
    }, [isLoading, venueId]);

    // Silent auto-refresh every 5 minutes. Reuses getSuccess/getError so it quietly swaps in fresh
    // data without the Refresh button's spinner. Interval is cleared on unmount.
    useEffect(() => {
        if (isLoading || venueId === null) {
            return;
        }
        const intervalId = setInterval(() => {
            findPreviousStages(venueId, getSuccess, getError, getAccessTokenSilently);
        }, 300000); // 5 minutes

        return () => clearInterval(intervalId);
    }, [isLoading, venueId]);

    const refresh = () => {
        if (venueId === null || isRefreshing) {
            return;
        }
        setErrorMessage(null);
        setIsRefreshing(true);
        findPreviousStages(venueId, getSuccess, getError, getAccessTokenSilently);
    };

    return (
        <section className="overflow-hidden bg-gray-50 dark:bg-gray-900 py-12 md:py-16 lg:py-20 min-h-screen">
            <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100 sm:text-5xl">
                        Previous <span className="text-pink-400">Stage</span>
                    </h1>
                    <p className="mt-3 text-base text-gray-500 dark:text-gray-400">
                        The last 20 dancers on stage, their stage tips, and table requests.
                    </p>
                    <div className="mt-5 flex justify-center">
                        <button
                            type="button"
                            onClick={refresh}
                            disabled={isRefreshing || isPageLoading}
                            className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isRefreshing ? "Refreshing..." : "Refresh"}
                            {isRefreshing && <CircularProgress color="inherit" size={16}/>}
                        </button>
                    </div>
                </div>

                <div className="mt-8">
                    {errorMessage && <LoginError errorMessage={errorMessage}/>}
                </div>

                {isPageLoading && (
                    <div className="mt-8 space-y-4">
                        <Skeleton variant="rounded" height={120}/>
                        <Skeleton variant="rounded" height={120}/>
                        <Skeleton variant="rounded" height={120}/>
                    </div>
                )}

                {!isPageLoading && previousStages && previousStages.length === 0 && (
                    <div className="mt-12 text-center text-lg text-gray-500 dark:text-gray-400">
                        No previous stage data yet.
                    </div>
                )}

                {!isPageLoading && previousStages && previousStages.length > 0 && (
                    <ul className="mt-8 space-y-5">
                        {previousStages.map((stage, index) => (
                            <li
                                key={stage.stageSessionId || index}
                                className="overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-sm ring-1 ring-gray-100 dark:ring-gray-700"
                            >
                                <div className="flex items-center justify-between bg-gray-900 px-5 py-4">
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wide text-pink-300">
                                            On stage
                                        </p>
                                        <p className="text-2xl font-bold text-white">
                                            {stage.dancerName || "Unknown"}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-medium uppercase tracking-wide text-pink-300">
                                            Stage Tips
                                        </p>
                                        <p className="text-2xl font-bold text-white">
                                            <NumericFormat
                                                value={stage.totalTips || 0}
                                                displayType={'text'}
                                                thousandSeparator={true}
                                                prefix={'$'}
                                            />
                                        </p>
                                    </div>
                                </div>

                                <div className="px-5 py-4">
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        Table Requests
                                    </p>
                                    {(!stage.tableRequests || stage.tableRequests.length === 0) ? (
                                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No table requests.</p>
                                    ) : (
                                        <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
                                            {stage.tableRequests.map((request, rIndex) => (
                                                <li
                                                    key={rIndex}
                                                    className="flex items-center justify-between py-2"
                                                >
                                                    <span className="flex items-center gap-3">
                                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-sm">
                                                            🪑
                                                        </span>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                            Table {request.tableId || "—"}
                                                        </span>
                                                    </span>
                                                    {request.completed ? (
                                                        <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                                                            Completed
                                                        </span>
                                                    ) : request.seen ? (
                                                        <span className="rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-xs font-semibold text-gray-600 dark:text-gray-400">
                                                            Seen
                                                        </span>
                                                    ) : (
                                                        <span className="rounded-full bg-pink-100 px-3 py-1 text-xs font-semibold text-pink-700">
                                                            New
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
